import React, { ReactNode, useEffect, useRef, useState, type FC } from "react";
import {
  ButtonProps,
  CircularProgress,
  TableCellProps,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";

import {
  StyledCard,
  StyledCardContent,
  StyledCenteredBox,
} from "./CardTable.styled";
import CardTableContainer from "./components/CardTableContainer/CardTableContainer";

import { Box, Button, Stack } from "@components/base";

export type CardTableColumn = {
  field: string;
  title: string | ReactNode;
  tableProps?: TableCellProps;
};

export type CardTableRow = {
  [key: string]: string | ReactNode;
};

interface CardTable {
  type: string;
  title: string;
  columns: CardTableColumn[];
  rows: CardTableRow[];
  isLoaded: boolean;
  placeholderText?: string | ReactNode;
  button?: {
    variant: ButtonProps["variant"];
    text: string;
    onClick(): void;
    disabled?: boolean;
    loading?: boolean;
  };
}

const CardTable: FC<CardTable> = ({
  type,
  title,
  columns,
  rows,
  isLoaded,
  placeholderText,
  button,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(300);

  // TODO: delete - replace with css?
  const getTableHeight = (): number =>
    cardRef.current ? cardRef.current.clientHeight - 80 : 100;

  useEffect(() => {
    const handleWindowResize = (): void => {
      setTableHeight(getTableHeight());
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  return (
    <StyledCard ref={cardRef}>
      <Stack spacing={2} direction="row" justifyContent="space-between" p={3}>
        <Typography variant="subtitle1">{title}</Typography>
        {button && (
          <Button
            variant={button.variant}
            onClick={button.onClick}
            disabled={button.disabled}
            isLoading={button.loading}
            size="small"
          >
            {button.text}
          </Button>
        )}
      </Stack>
      <StyledCardContent
        sx={{
          height: tableHeight,
        }}
      >
        {isLoaded ? (
          !isEmpty(rows) ? (
            <CardTableContainer columns={columns} rows={rows} type={type} />
          ) : (
            <StyledCenteredBox>
              <img alt="search" height={100} width={130} src="/static/no-data.svg" />
              {placeholderText && (
                <Box mt={2} textAlign="center">
                  {placeholderText}
                </Box>
              )}
            </StyledCenteredBox>
          )
        ) : (
          <StyledCenteredBox>
            <CircularProgress />
          </StyledCenteredBox>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default CardTable;
